import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { UseQueryResult } from "@tanstack/react-query";
import { StatusBadge } from "@/components/status-badge";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { errorMessage } from "../../api";
import { ErrorBlock, FetchingDot, LoadingBlock, SectionCard } from "../../components/primitives";
import { TimeSeriesChart, type ChartSeries } from "../../components/time-series-chart";
import { formatCount } from "../../format";
import type { MetricSeries, Verdict } from "../../types";
import { VERDICT_TONE, type Tone } from "./helpers";

/** Building blocks shared by the camera and pizza flow tabs. */

const TONE_VALUE: Record<Tone, string> = {
  neutral: "text-foreground",
  warn: "text-amber-300",
  nok: "text-red-400",
};

/** One number of a summary strip: calm unless it marks a problem. */
export function StatTile({
  label,
  value,
  detail,
  tone = "neutral",
  hint,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
  hint?: string;
  className?: string;
}) {
  return (
    <div
      title={hint}
      className={cn(
        "min-w-0 rounded-lg border bg-card px-3 py-2.5",
        tone === "nok" && "border-red-500/40",
        tone === "warn" && "border-amber-500/35",
        className,
      )}
    >
      <div className="break-words text-[11px] font-medium uppercase leading-tight tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn("mt-1 break-words text-xl font-semibold leading-tight tabular-nums", TONE_VALUE[tone])}>{value}</div>
      {detail && <div className="mt-0.5 break-words text-[11px] leading-snug text-muted-foreground">{detail}</div>}
    </div>
  );
}

const CHIP_TONE: Record<Tone, string> = {
  neutral: "border-border text-muted-foreground",
  warn: "border-amber-500/35 text-amber-300",
  nok: "border-red-500/40 bg-red-500/15 text-red-300",
};

/** Small outlined tag (branch flags, problem reasons). */
export function Chip({ children, tone = "neutral", title, className }: { children: ReactNode; tone?: Tone; title?: string; className?: string }) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex min-h-5 max-w-full items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium leading-tight",
        CHIP_TONE[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A count that is calm at zero and coloured when it is a problem. */
export function ProblemCount({ value, tone = "nok", className }: { value: number | null | undefined; tone?: Exclude<Tone, "neutral">; className?: string }) {
  const active = (value ?? 0) > 0;
  return (
    <span
      className={cn(
        "tabular-nums",
        active ? cn("font-semibold", TONE_VALUE[tone]) : "text-muted-foreground",
        className,
      )}
    >
      {formatCount(value ?? 0)}
    </span>
  );
}

/** Verdict of a pizza: OK and "evaluating" as quiet text, problems as coloured badges. */
export function VerdictBadge({ verdict, className }: { verdict: Verdict; className?: string }) {
  const { t } = useLocalization();
  return (
    <StatusBadge
      status={verdict}
      tone={VERDICT_TONE[verdict]}
      label={t(`monitoring.verdict.${verdict}`)}
      size="xs"
      quiet={verdict === "ok" || verdict === "pending"}
      className={className}
    />
  );
}

/** Pizza name linking to its detail, with the id below. */
export function PizzaRef({ id, name, className }: { id: string; name: string | null | undefined; className?: string }) {
  const { t } = useLocalization();
  return (
    <div className={cn("min-w-0", className)}>
      <Link to={`/pizzas/${id}`} className="break-words text-sm font-medium leading-snug hover:underline">
        {name || t("monitoring.pipeline.unnamedPizza")}
      </Link>
      <div className="break-all font-mono text-[10px] leading-tight text-muted-foreground">{id}</div>
    </div>
  );
}

/** How a branch captures: photos on / off and who fires the camera (BiteBerry packing step or the app). */
export function BranchFlags({
  acquisition,
  bbTrigger,
  className,
}: {
  acquisition: boolean | null | undefined;
  bbTrigger: boolean | null | undefined;
  className?: string;
}) {
  const { t } = useLocalization();
  if (acquisition === null || acquisition === undefined) return null;
  return (
    <div className={cn("mt-1 flex flex-wrap gap-1", className)}>
      <Chip
        title={t(acquisition ? "monitoring.cameras.flag.captureOnHint" : "monitoring.cameras.flag.captureOffHint")}
        className={acquisition ? undefined : "opacity-70"}
      >
        {t(acquisition ? "monitoring.cameras.flag.captureOn" : "monitoring.cameras.flag.captureOff")}
      </Chip>
      {acquisition && bbTrigger !== null && bbTrigger !== undefined && (
        <Chip title={t(bbTrigger ? "monitoring.cameras.flag.bbTriggerHint" : "monitoring.cameras.flag.manualTriggerHint")}>
          {t(bbTrigger ? "monitoring.cameras.flag.bbTrigger" : "monitoring.cameras.flag.manualTrigger")}
        </Chip>
      )}
    </div>
  );
}

/** "label value" line inside a table cell or card. */
export function InlineStat({ label, children, className, title }: { label: ReactNode; children: ReactNode; className?: string; title?: string }) {
  return (
    <div className={cn("flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-xs leading-snug", className)} title={title}>
      <span className="shrink-0 text-[11px] text-muted-foreground">{label}</span>
      <span className="min-w-0 tabular-nums">{children}</span>
    </div>
  );
}

/** Time chart of in-process metrics with its loading / error states. */
export function MetricChartCard({
  title,
  description,
  query,
  build,
  format,
  tickFormat,
  integer,
  height = 200,
  footer,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  query: UseQueryResult<MetricSeries>;
  /** must be stable (useCallback / module function) */
  build: (metrics: MetricSeries) => ChartSeries[];
  format?: (value: number) => string;
  tickFormat?: (value: number) => string;
  integer?: boolean;
  height?: number;
  footer?: (metrics: MetricSeries) => ReactNode;
  className?: string;
}) {
  const { t } = useLocalization();
  const data = query.data;
  const series = useMemo(() => (data ? build(data) : []), [data, build]);
  return (
    <SectionCard
      title={title}
      description={description}
      actions={<FetchingDot active={query.isFetching && !query.isPending} />}
      className={className}
    >
      {data ? (
        <div className="flex flex-col gap-3">
          {query.isError && <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />}
          <TimeSeriesChart
            t={data.t}
            stepMs={data.step_ms}
            window={data.window}
            series={series}
            height={height}
            format={format}
            tickFormat={tickFormat}
            integer={integer}
            emptyText={t("monitoring.noData")}
          />
          {footer?.(data)}
        </div>
      ) : query.isError ? (
        <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      ) : (
        <LoadingBlock height={height} />
      )}
    </SectionCard>
  );
}

/** Totals of the series of a chart as one muted line: "Triggered 120 · Trigger failed 2". */
export function SeriesTotals({ items, title }: { items: { label: string; value: ReactNode; tone?: Tone }[]; title?: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {title && <span className="font-medium text-foreground/80">{title}</span>}
      {items.map((item) => (
        <span key={item.label} className="whitespace-nowrap">
          {item.label}{" "}
          <span className={cn("font-mono tabular-nums", item.tone && item.tone !== "neutral" ? TONE_VALUE[item.tone] : "text-foreground")}>
            {item.value}
          </span>
        </span>
      ))}
    </div>
  );
}
